import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import authConfig from 'config/auth'
import { persist } from 'easy-peasy'
import checkToken from 'lib/auth/check-token'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { ToastContainer } from 'react-toastify'
import { useStoreActions, useStoreState } from 'stores/hooks'
import store from 'stores/index'
import styled from 'styled-components'

interface LayoutType {
  header?: any
  footer?: any
  children?: any
  seo?: any
  responsive?: boolean
  fluid?: boolean
}

const StyledContainer = styled.div`
  width: 100%;
  ${(props) =>
    props.responsive
      ? `
    margin-right: auto;
    margin-left: auto;  
    @media (min-width: 576px) {
      max-width: 540px;
    }
    @media (min-width: 768px) {
      max-width: 720px;
    }
    @media (min-width: 992px) {
      max-width: 960px;
    }
    @media (min-width: 1200px) {
      max-width: 1140px;
    }
    @media (min-width: 1400px) {
      max-width: 1320px;
    }
  `
      : `min-width: 1180px;`}
  ${(props) => props.fluid && `min-width: 100% !important;`}
`

const StyledToastContainer = styled(ToastContainer).attrs({
  className: 'toast-container',
  toastClassName: 'toast',
})`
  margin: 30px 16px 0;
  display: inline-table;
  width: fit-content;
  max-width: 580px;
  min-width: 300px;

  .toast {
    --toastify-color-success: ${theme.alert.success.background};
    --toastify-color-error: ${theme.alert.error.background};
    --toastify-color-info: ${theme.alert.info.background};
  }
`

function Layout({
  header = null,
  footer = null,
  children = null,
  seo = {},
  responsive = false,
  fluid = false,
  ...otherProps
}: LayoutType): JSX.Element {
  const { pathname } = useRouter()

  // const { globalMaintenanceMode } = useFlags() // import { useFlags } from launchdarkly-react-client-sdk
  // if (globalMaintenanceMode === true) redirect('/maintenance.html')

  const { getUserProfile, setLoading } = useStoreActions(
    (actions) => actions.profile,
  )
  const { profileData } = useStoreState((actions) => actions.profile)

  const accessToken = checkToken(authConfig.accessTokenName)
  const refreshToken = checkToken(authConfig.refreshTokenName)

  useEffect(() => {
    // Clear profile store when user on access token doesnt exist and profile data exist
    if (
      !accessToken &&
      !refreshToken &&
      Object.keys(profileData).length !== 0
    ) {
      store.getActions().profile.setUserProfile(persist(null))
    }
    if (Object.keys(profileData).length !== 0) setLoading(false)
    getUserProfile()
  }, [])
  return (
    <StyledContainer
      responsive={responsive}
      fluid={fluid}
      {...otherProps}
    >
      <NextSeo
        canonical={`${WEB_DESKTOP_RALALI_URL}${pathname}`}
        {...seo}
        additionalMetaTags={[
          {
            name: 'viewport',
            content: responsive
              ? 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no'
              : 'width=device-width, initial-scale=0',
          },
          ...(seo?.additionalMetaTags || []),
        ]}
      />
      {header && header.component}
      <StyledToastContainer />
      <main>{children}</main>
      {footer && footer.component}
    </StyledContainer>
  )
}

export default Layout
